// 内容区 Spinner / skeleton 工具 / Content-area spinner + skeleton helpers.
// 服务于 AGENTS.md 的全局硬约束：所有需要后端数据载入的 UI 必须先渲染空骨架 + Spinner，
// 等数据返回后再填充内容，禁止"点击后卡住，等数据回来才一次性弹出"。
// Supports the AGENTS.md rule: every data-loading UI must paint an empty skeleton
// with a Spinner in the same tick as the trigger, then swap in real content once
// the fetch resolves. No synchronous blocking before the first frame.

/**
 * 创建一个内容区 Spinner（比按钮 Spinner 大，专用于面板/浮层内容区）。
 * Build a content-area spinner node (larger than the button spinner,
 * intended for panels / modal bodies / card grids).
 */
export function createSpinner(size: "sm" | "md" | "lg" = "md"): HTMLElement {
  const span = document.createElement("span");
  span.className = `content-spinner content-spinner-${size}`;
  span.setAttribute("aria-hidden", "true");
  return span;
}

/**
 * 创建"Spinner + 可选文案"的加载块，适合放进浮层内容区、列表空容器等。
 * Build a "spinner + optional label" loading block suitable for modal
 * bodies and empty list containers.
 */
export function createLoadingBlock(label?: string): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "content-loading";
  wrap.setAttribute("role", "status");
  wrap.setAttribute("aria-live", "polite");
  wrap.append(createSpinner("md"));
  if (label) {
    const text = document.createElement("span");
    text.className = "content-loading-label";
    text.textContent = label;
    wrap.append(text);
  }
  return wrap;
}

/**
 * 创建卡片网格骨架：指定数量的占位卡片（无内容，带 shimmer 动画）。
 * Build a card grid skeleton: N placeholder cards with a shimmer animation,
 * for workbench mode-switches that need to paint a scaffold immediately.
 */
export function createCardSkeletonGrid(count = 4): HTMLElement {
  const grid = document.createElement("div");
  grid.className = "workbench-card-grid workbench-card-grid-skeleton";
  grid.setAttribute("aria-busy", "true");
  for (let i = 0; i < Math.max(1, count); i += 1) {
    const card = document.createElement("div");
    card.className = "workbench-card-shell workbench-card-shell-skeleton";
    card.innerHTML = `
      <div class="workbench-card-main">
        <span class="skeleton-line skeleton-line-kicker"></span>
        <span class="skeleton-line skeleton-line-title"></span>
        <span class="skeleton-line"></span>
        <span class="skeleton-line skeleton-line-short"></span>
      </div>
    `;
    grid.append(card);
  }
  return grid;
}
